import {
  CashIcon,
  CogIcon,
  CurrencyDollarIcon,
  HomeIcon,
  LocationMarkerIcon,
  UserIcon
} from "@heroicons/react/outline";
import { get, values } from "lodash";
import React, { useMemo } from "react";
import {
  matchPath,
  Redirect,
  Route,
  RouteComponentProps,
  Switch
} from "react-router-dom";
import BoxIcon from "../../components/icons/BoxIcon";
import PieChartIcon from "../../components/icons/PieChartIcon";
import Molecule from "../../components/Molecule";
import NavLink from "../../components/NavLink";
import { useCommissionReports } from "../../context/commissionReports";
import { useContacts } from "../../context/contacts";
import { useHotspots } from "../../context/hotspots";
import { useInstalls } from "../../context/installs";
import { useLocations } from "../../context/locations";
import { useWallets } from "../../context/wallets";
import { useWorkspaces } from "../../context/workspaces";
import AppShell from "../../layout/AppShell";
import PrivateRoute from "../../layout/PrivateRoute";
import classNames from "../../utils/classNames";
import PlansFailed from "../Plans/PlansFailed";
import PlansSuccess from "../Plans/PlansSuccess";
import CommissionReportsData from "./CommissionReportsData";
import ContactsData from "./ContactsData";
import Dashboard from "./Dashboard";
import HotspotsData from "./HotspotsData";
import InstallsData from "./InstallsData";
import LocationsData from "./LocationsData";
import Settings from "./Settings";
import WalletsData from "./WalletsData";

interface Props extends RouteComponentProps {}

const Workspace: React.FC<Props> = (props) => {
  const {
    location: { pathname }
  } = props;
  const { currentWorkspaceId, workspaces, currentWorkspace } = useWorkspaces();
  const { workspaceHotspots } = useHotspots();
  const { locations } = useLocations();
  const { wallets } = useWallets();
  const { contacts } = useContacts();
  const { installs } = useInstalls();
  const { commissionReports } = useCommissionReports();

  const pageMatch = matchPath<{
    section: string;
  }>(pathname, {
    path: "/workspace/:section"
  });

  const tabs = useMemo(
    () => [
      {
        key: "dashboard",
        title: "Dashboard",
        Icon: PieChartIcon,
        count: 0
      },
      {
        key: "wallets",
        title: "Wallets",
        Icon: CashIcon,
        count: currentWorkspace?.counts?.wallet ?? values(wallets).length
      },
      {
        key: "hotspots",
        title: "Hotspots",
        Icon: BoxIcon,
        count: currentWorkspace?.counts?.hotspot ?? workspaceHotspots.length,
        allowedCount: currentWorkspace?.active_plan?.allowed_hotspots ?? 0
      },
      {
        key: "locations",
        title: "Locations",
        Icon: LocationMarkerIcon,
        count: currentWorkspace?.counts?.location ?? values(locations).length,
        allowedCount: currentWorkspace?.active_plan?.allowed_locations ?? 0
      },
      {
        key: "contacts",
        title: "Contacts",
        Icon: UserIcon,
        count: currentWorkspace?.counts?.contact ?? values(contacts).length
      },
      {
        key: "installs",
        title: "Installs",
        Icon: HomeIcon,
        count: currentWorkspace?.counts?.install ?? values(installs).length
      },
      {
        key: "commission-reports",
        title: "Commission reports",
        Icon: CurrencyDollarIcon,
        count:
          currentWorkspace?.counts?.commissionReport ??
          values(commissionReports).length
      },
      {
        key: "settings",
        title: "Settings",
        Icon: CogIcon,
        count: 0
      }
    ],
    [
      commissionReports,
      contacts,
      currentWorkspace?.active_plan?.allowed_hotspots,
      currentWorkspace?.active_plan?.allowed_locations,
      currentWorkspace?.counts?.commissionReport,
      currentWorkspace?.counts?.contact,
      currentWorkspace?.counts?.hotspot,
      currentWorkspace?.counts?.install,
      currentWorkspace?.counts?.location,
      currentWorkspace?.counts?.wallet,
      installs,
      locations,
      wallets,
      workspaceHotspots.length
    ]
  );

  return (
    <AppShell
      {...props}
      currentPage={get(
        workspaces,
        [currentWorkspaceId || "", "name"],
        "Workspace"
      )}
    >
      <Molecule className="fixed right-0 top-0" />
      <div>
        <div className="sm:hidden block p-4">
          <select
            className="w-full pl-3 py-2 sm:text-sm rounded-md dark:bg-gray-800 relative z-1"
            onChange={(e) => {
              props.history.push(`${props.match.url}/${e.target.value}`);
            }}
            value={pageMatch?.params.section}
          >
            {tabs.map(({ key, title, count, allowedCount }) => {
              let text = title;

              if (count || allowedCount != null) {
                text +=
                  allowedCount != null
                    ? ` (${count.toLocaleString(
                        undefined
                      )}/${allowedCount.toLocaleString(undefined)})`
                    : `(${count.toLocaleString(undefined)})`;
              }

              return (
                <option key={key} value={key}>
                  {text}
                </option>
              );
            })}
          </select>
        </div>

        <div className="hidden sm:block relative">
          <div className="border-b border-gray-200 dark:border-gray-500 flex-shrink-0 justify-between dark:bg-gray-900">
            <nav className="-mb-px flex" aria-label="Tabs">
              {tabs.map(({ key, title, count, allowedCount, Icon }) => {
                let countText;

                if (count || allowedCount != null) {
                  countText =
                    allowedCount != null
                      ? `${count.toLocaleString(
                          undefined
                        )}/${allowedCount.toLocaleString(undefined)}`
                      : count.toLocaleString(undefined);
                }
                return (
                  <NavLink
                    key={key}
                    to={`${props.match.url}/${key}`}
                    activeClassName="border-hotspotty-400 text-hotspotty-500 dark:text-white dark:border-white"
                    inactiveClassName="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                    className="whitespace-nowrap flex py-4 px-4 border-b-2 font-medium text-sm group"
                  >
                    <Icon
                      className={classNames(
                        pageMatch?.params.section === key
                          ? "text-hotspotty-400 dark:text-white"
                          : "text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300",
                        "-ml-0.5 mr-2 h-5 w-5"
                      )}
                    />
                    <span>{title}</span>
                    {countText ? (
                      <span
                        className={classNames(
                          pageMatch?.params.section === key
                            ? "bg-hotspotty-400 text-gray-100 dark:bg-gray-700"
                            : "bg-gray-200 text-gray-500 group-hover:text-gray-700 dark:text-gray-200 dark:bg-gray-600 dark:group-hover:text-gray-200 dark:group-hover:bg-gray-500",
                          "hidden ml-3 py-0.5 px-2.5 rounded-full text-xs font-medium md:inline-block"
                        )}
                      >
                        {countText}
                      </span>
                    ) : null}
                  </NavLink>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      <div
        style={{ zIndex: 5 }}
        className="flex flex-col overflow-y-auto h-full"
      >
        <Switch>
          <PrivateRoute
            path={`${props.match.path}/dashboard`}
            component={Dashboard}
          />
          <PrivateRoute
            path={`${props.match.path}/hotspots`}
            component={HotspotsData}
          />
          <PrivateRoute
            path={`${props.match.path}/locations`}
            component={LocationsData}
          />
          <PrivateRoute
            path={`${props.match.path}/wallets`}
            component={WalletsData}
          />
          <PrivateRoute
            path={`${props.match.path}/contacts`}
            component={ContactsData}
          />
          <PrivateRoute
            path={`${props.match.path}/installs`}
            component={InstallsData}
          />
          <PrivateRoute
            path={`${props.match.path}/commission-reports`}
            component={CommissionReportsData}
          />
          <PrivateRoute
            path={`${props.match.path}/settings`}
            component={Settings}
          />

          <Route
            path={`${props.match.path}/pricing/success`}
            component={PlansSuccess}
          />

          <Route
            path={`${props.match.path}/pricing/failed`}
            component={PlansFailed}
          />

          <Redirect
            exact
            from="/workspace"
            to={`${props.match.path}/dashboard`}
          />
        </Switch>
      </div>
    </AppShell>
  );
};

export default Workspace;
